import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AuthShell from './AuthShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { fetchMe, register } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';

export default function Register() {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { hydrate } = useAuth();

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setPending(true);
    try {
      const fd = new FormData(event.currentTarget);
      await register({
        email: String(fd.get('email') ?? '').trim(),
        password: String(fd.get('password') ?? ''),
        fullName: String(fd.get('fullName') ?? '').trim(),
        accountName: String(fd.get('accountName') ?? '').trim(),
        propertyName:
          String(fd.get('propertyName') ?? '').trim() || undefined,
      });
      const me = await fetchMe();
      hydrate(me);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not register');
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthShell
      stamp="Create your account"
      title="HouseOps"
      subtitle="One home, one record. Make a private workspace for it."
      footer={
        <span>
          Already have an account?{' '}
          <Link to="/auth/sign-in" className="text-primary hover:underline">
            Sign in
          </Link>
        </span>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
        <div className="space-y-2">
          <Label htmlFor="fullName" className="font-mono text-[11px] uppercase tracking-wider">
            Your name
          </Label>
          <Input
            id="fullName"
            name="fullName"
            type="text"
            autoComplete="name"
            required
            className="h-11 text-base"
            data-testid="fullName"
            autoFocus
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email" className="font-mono text-[11px] uppercase tracking-wider">
            Email
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            className="h-11 text-base"
            data-testid="email"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password" className="font-mono text-[11px] uppercase tracking-wider">
            Password
          </Label>
          <Input
            id="password"
            name="password"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            className="h-11 text-base"
            data-testid="password"
          />
          <p className="text-xs text-muted-foreground">At least 8 characters.</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="accountName" className="font-mono text-[11px] uppercase tracking-wider">
            Account name
          </Label>
          <Input
            id="accountName"
            name="accountName"
            type="text"
            required
            className="h-11 text-base"
            placeholder="The Taylor House"
            data-testid="accountName"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="propertyName" className="font-mono text-[11px] uppercase tracking-wider">
            First property name
          </Label>
          <Input
            id="propertyName"
            name="propertyName"
            type="text"
            className="h-11 text-base"
            placeholder="My House"
            data-testid="propertyName"
          />
          <p className="text-xs text-muted-foreground">
            Optional. Defaults to "My House"; you can rename or add more later.
          </p>
        </div>
        {error ? (
          <p className="text-sm text-warning font-medium" role="alert">
            {error}
          </p>
        ) : null}
        <Button type="submit" className="w-full h-11 text-base" disabled={pending} data-testid="submit">
          {pending ? 'Creating account…' : 'Create account'}
        </Button>
      </form>
    </AuthShell>
  );
}
